import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, KeyRound, Plus, ShieldCheck, UserPlus, Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SINGLE = ["cashier", "waiter", "kitchen"];
const ROLE_NAMES = { manager: "Branch Manager", cashier: "Cashier", waiter: "Waiter", kitchen: "Kitchen" };
const roleChoices = (session) => [["cashier", "Cashier"], ["manager", "Branch Manager"], ...(session?.business?.business_type === "restaurant" ? [["waiter", "Waiter"], ["kitchen", "Kitchen"]] : [])];

function Section({ icon: Icon, title, action, children, id }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`team-section-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-amber-600" /><h2 className="font-display text-xl font-bold text-slate-900">{title}</h2></div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function FormDialog({ open, title, onClose, onSubmit, error, busy, children, testid }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-md overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          {children}
          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid={`${testid}-error`}>{error}</div>}
          <button disabled={busy} data-testid={`${testid}-submit`} className="h-12 w-full rounded-xl bg-slate-900 font-semibold text-white disabled:bg-slate-300">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, ...p }) => (<div className="space-y-1.5"><Label>{label}</Label><Input className="h-11" {...p} /></div>);

function useSubmit(fn, after) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      await fn();
      after();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

function Branches({ branches, reload }) {
  const { refresh } = useAuth();
  const [form, setForm] = useState(null);
  const sub = useSubmit(
    () => (form.id ? api.put(`/branches/${form.id}`, form) : api.post("/branches", { ...form, code: form.code || null })),
    () => { toast.success("Branch saved"); setForm(null); reload(); refresh(); }
  );
  const open = (b) => { sub.setError(""); setForm(b ? { id: b.id, name: b.name, address: b.address, phone: b.phone, code: b.code } : { name: "", address: "", phone: "", code: "" }); };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Section id="branches" icon={Building2} title="Branches" action={<button onClick={() => open(null)} data-testid="add-branch-button" className="flex h-10 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-sm font-bold text-slate-950"><Plus className="h-4 w-4" /> Add branch</button>}>
      <ul className="divide-y divide-slate-100">
        {branches.map((b) => (
          <li key={b.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`branch-row-${b.code}`}>
            <span className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[11px] font-bold text-amber-400">{b.code}</span>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900">{b.name}{b.is_default && <span className="ml-2 text-xs text-slate-400">default</span>}</div>
              <div className="text-xs text-slate-500">{[b.address, b.phone].filter(Boolean).join(" · ") || "No address yet"}</div>
            </div>
            <span className="font-mono text-xs text-slate-500">next {b.code}-{String((b.receipt_seq || 0) + 1).padStart(6, "0")}</span>
            <button onClick={() => open(b)} data-testid={`edit-branch-${b.code}`} className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold hover:border-slate-900">Edit</button>
          </li>
        ))}
      </ul>
      <FormDialog open={!!form} title={form?.id ? "Edit branch" : "Add branch"} onClose={() => setForm(null)} onSubmit={sub.run} error={sub.error} busy={sub.busy} testid="branch-form">
        {form && (<>
          <Field label="Name" value={form.name} onChange={set("name")} data-testid="branch-name-input" />
          <Field label="Address" value={form.address} onChange={set("address")} data-testid="branch-address-input" />
          <Field label="Phone" value={form.phone} onChange={set("phone")} data-testid="branch-phone-input" />
          <Field label="Receipt prefix (optional)" placeholder="e.g. CLF" value={form.code} onChange={(e) => setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))} data-testid="branch-code-input" />
        </>)}
      </FormDialog>
    </Section>
  );
}

function StaffWhatsApp({ uid }) {
  const [v, setV] = useState(null);
  useEffect(() => { api.get(`/staff/${uid}/whatsapp`).then((r) => setV(r.data.whatsapp || "")).catch(() => setV("")); }, [uid]);
  const save = async () => {
    try { const { data } = await api.put(`/staff/${uid}/whatsapp`, { whatsapp: v.trim() || null }); setV(data.whatsapp || ""); toast.success("WhatsApp number saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (v === null) return null;
  return (
    <div className="space-y-1.5">
      <Label>WhatsApp number (optional, for password reset)</Label>
      <div className="flex gap-2">
        <Input type="tel" placeholder="+923001234567" value={v} onChange={(e) => setV(e.target.value)} className="h-11" data-testid="staff-whatsapp-input" />
        <button type="button" onClick={save} className="h-11 shrink-0 rounded-xl border-2 border-slate-900 px-4 text-sm font-semibold" data-testid="staff-whatsapp-save">Save</button>
      </div>
    </div>
  );
}

function Staff({ branches }) {
  const { session } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState(null);
  const load = useCallback(() => api.get("/staff").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const sub = useSubmit(
    () => (form.id ? api.put(`/staff/${form.id}`, { name: form.name, username: form.username, role: form.role, branch_ids: form.branch_ids }) : api.post("/staff", form)),
    () => { toast.success("Staff saved"); setForm(null); load(); }
  );
  const pwSub = useSubmit(() => api.post(`/staff/${pw.id}/reset-password`, { password: pw.password }), () => { toast.success("Password reset"); setPw(null); });
  const open = (u) => { sub.setError(""); setForm(u ? { id: u.id, name: u.name, username: u.username, role: u.role, branch_ids: u.branch_ids } : { name: "", username: "", password: "", role: "cashier", branch_ids: [] }); };
  const toggleBranch = (id) => setForm((s) => ({ ...s, branch_ids: SINGLE.includes(s.role) ? [id] : s.branch_ids.includes(id) ? s.branch_ids.filter((x) => x !== id) : [...s.branch_ids, id] }));
  const toggleActive = async (u) => {
    try {
      await api.post(`/staff/${u.id}/${u.is_active ? "deactivate" : "reactivate"}`);
      toast.success(u.is_active ? `${u.username} deactivated` : `${u.username} reactivated`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section id="staff" icon={Users} title="Staff" action={<button onClick={() => open(null)} data-testid="add-staff-button" className="flex h-10 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-sm font-bold text-slate-950"><UserPlus className="h-4 w-4" /> Add staff</button>}>
      {rows.length === 0 && <div className="py-4 text-sm text-slate-400">No staff yet.</div>}
      <ul className="divide-y divide-slate-100">
        {rows.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`staff-row-${u.username}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900">{u.name} <span className="font-mono text-xs text-slate-500">@{u.username}</span></div>
              <div className="text-xs text-slate-500">{u.role_label} · {u.branch_names.join(", ")}</div>
            </div>
            <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${u.is_active ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`} data-testid={`staff-status-${u.username}`}>{u.is_active ? "Active" : "Disabled"}</span>
            <button onClick={() => open(u)} data-testid={`staff-edit-${u.username}`} className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold hover:border-slate-900">Edit</button>
            <button onClick={() => { pwSub.setError(""); setPw({ id: u.id, username: u.username, password: "" }); }} data-testid={`staff-reset-${u.username}`} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 hover:border-slate-900" aria-label="Reset password"><KeyRound className="h-4 w-4" /></button>
            <button onClick={() => toggleActive(u)} data-testid={`staff-toggle-${u.username}`} className={`h-9 rounded-lg px-3 text-sm font-semibold ${u.is_active ? "text-rose-600 hover:bg-rose-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{u.is_active ? "Deactivate" : "Reactivate"}</button>
          </li>
        ))}
      </ul>
      <FormDialog open={!!form} title={form?.id ? "Edit staff" : "Add staff"} onClose={() => setForm(null)} onSubmit={sub.run} error={sub.error} busy={sub.busy} testid="staff-form">
        {form && (<>
          <Field label="Name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="staff-name-input" />
          <Field label="Username" autoCapitalize="none" value={form.username} onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))} data-testid="staff-username-input" />
          {!form.id && <Field label="Password" type="password" value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} data-testid="staff-password-input" />}
          <div className="space-y-1.5">
            <Label>Role</Label>
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
              {roleChoices(session).map(([k, l]) => (
                <button type="button" key={k} onClick={() => setForm((s) => ({ ...s, role: k, branch_ids: SINGLE.includes(k) ? s.branch_ids.slice(0, 1) : s.branch_ids }))} data-testid={`staff-role-${k}`} className={`h-10 rounded-lg text-sm font-semibold ${form.role === k ? "bg-white shadow-sm" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{SINGLE.includes(form.role) ? "Branch (exactly one)" : "Branches (one or more)"}</Label>
            <div className="flex flex-wrap gap-2">
              {branches.map((b) => {
                const on = form.branch_ids.includes(b.id);
                return <button type="button" key={b.id} onClick={() => toggleBranch(b.id)} data-testid={`staff-branch-${b.code}`} className={`h-10 rounded-lg border-2 px-3 text-sm font-semibold ${on ? "border-slate-900 bg-amber-50" : "border-slate-200 text-slate-500"}`}>{b.name}</button>;
              })}
            </div>
          </div>
          {form.id && <StaffWhatsApp uid={form.id} />}
        </>)}
      </FormDialog>
      <FormDialog open={!!pw} title={`Reset password · ${pw?.username || ""}`} onClose={() => setPw(null)} onSubmit={pwSub.run} error={pwSub.error} busy={pwSub.busy} testid="staff-password-form">
        {pw && <Field label="New password" type="password" value={pw.password} onChange={(e) => setPw((s) => ({ ...s, password: e.target.value }))} data-testid="staff-new-password-input" />}
      </FormDialog>
    </Section>
  );
}

function Permissions() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/permissions").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const toggle = async (role, perm, v) => {
    const roles = { ...data.roles, [role]: { ...data.roles[role], [perm]: v } };
    setData((d) => ({ ...d, roles }));
    try {
      await api.put("/permissions", roles);
      toast.success("Permissions updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section id="permissions" icon={ShieldCheck} title="Permissions">
      <p className="-mt-2 mb-3 text-sm text-slate-500">The owner always has every permission. Changes apply on staff's next action.</p>
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px] text-sm" data-testid="permissions-table">
            <thead><tr className="font-mono text-[10px] uppercase tracking-widest text-slate-500"><th className="py-2 text-left">Permission</th>{(data.roles_list || ["manager", "cashier"]).map((r) => <th key={r}>{ROLE_NAMES[r]}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.order.map((p) => (
                <tr key={p}>
                  <td className="py-3 font-semibold capitalize text-slate-800">{data.labels[p]}</td>
                  {(data.roles_list || ["manager", "cashier"]).map((r) => (
                    <td key={r} className="text-center"><Switch checked={!!data.roles[r][p]} onCheckedChange={(v) => toggle(r, p, v)} data-testid={`perm-${r}-${p}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export default function Team() {
  const { session } = useAuth();
  const [branches, setBranches] = useState(session.branches || []);
  const reload = useCallback(() => api.get("/branches").then((r) => setBranches(r.data)), []);
  useEffect(() => { reload(); }, [reload]);
  if (session.user.role !== "owner") return <Navigate to="/pos" replace />;
  return (
    <div className="h-full overflow-y-auto" data-testid="team-page">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-8">
        <div>
          <div className="eyebrow">Owner only</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Team & branches</h1>
        </div>
        <Branches branches={branches} reload={reload} />
        <Staff branches={branches} />
        <Permissions />
      </div>
    </div>
  );
}
